import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { FLEETHEALTH_FILTER_TYPES } from '@app-assets/constants/assets.constants';
import { DiagnosticsQueryParams, SearchTypeEnum, SelectedFilter } from '@app-assets/models/assets.model';
import { DataService } from '@app-core/services/data/data.service';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, skip, switchMap, takeUntil, tap } from 'rxjs/operators';

@Component({
  selector: 'app-select-search',
  templateUrl: './select-search.component.html',
  styleUrls: ['./select-search.component.scss'],
})
export class SelectSearchComponent implements OnInit {
  @Output()
  public selectedFilter = new EventEmitter<SelectedFilter>();
  @Output()
  public clearFilter = new EventEmitter<void>();

  public form: FormGroup;
  public filteredAssets = null;
  public showSpinner: boolean;
  public isInputReadonly: boolean;
  public isSearch: boolean = true;
  public disableSearch: boolean = true;
  public searchTypeEnum = SearchTypeEnum;
  public assetFilterTypeList = FLEETHEALTH_FILTER_TYPES().sort((a, b) => (a.label > b.label ? 1 : -1));

  private selectedDeviceId = '';
  private selectedAssetId = '';
  private ngUnSubscribe: Subject<void> = new Subject();

  constructor(public dataService: DataService, private route: ActivatedRoute, private router: Router) {}

  public ngOnInit(): void {
    const queryParams: DiagnosticsQueryParams = { ...this.route.snapshot.queryParams };

    this.form = new FormGroup({
      searchTypeControl: new FormControl(this.searchTypeEnum.AssetIdFilter),
      deviceIdControl: new FormControl(queryParams?.deviceId || ''),
      assetInputControl: new FormControl(),
    });

    if (queryParams?.deviceId) {
      this.form.patchValue({
        searchTypeControl: this.searchTypeEnum.DeviceIdFilter,
      });
      this.disableSearch = true;
      this.isSearch = false;
      this.isInputReadonly = true;
    }

    this.dataService._currentFleet.pipe(takeUntil(this.ngUnSubscribe), skip(1)).subscribe(() => {
      this.form.patchValue({
        searchTypeControl: this.searchTypeEnum.AssetIdFilter,
        deviceIdControl: null,
        assetInputControl: null,
      });
      this.disableSearch = true;
      this.isSearch = true;
      this.isInputReadonly = false;
    });

    this.form
      .get('searchTypeControl')
      .valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((value) => {
        if (this.selectedAssetId || this.selectedDeviceId) {
          this.clearInput(true, value);
        } else {
          this.disableSearch = true;
          this.isSearch = true;
          this.selectedAssetId = null;
          this.selectedDeviceId = null;
          this.form.patchValue(
            {
              searchTypeControl: value,
              deviceIdControl: this.route.snapshot?.queryParams?.deviceId || null,
              assetInputControl: null,
            },
            { emitEvent: false }
          );
        }
      });

    this.form
      .get('deviceIdControl')
      .valueChanges.pipe(takeUntil(this.ngUnSubscribe))
      .subscribe((value) => {
        this.disableSearch = !(value?.trim()?.length > 0);
        this.isSearch = true;
      });

    this.form
      .get('assetInputControl')
      .valueChanges.pipe(
        takeUntil(this.ngUnSubscribe),
        debounceTime(500),
        map((val) => {
          const { fleetId = null } = val || {};
          const assetId = typeof val === 'string' ? val : val?.assetId;
          if (!fleetId && this.selectedAssetId) {
            this.disableSearch = true;
          } else if (fleetId && this.selectedAssetId) {
            this.disableSearch = false;
          }
          return (assetId || '').trim();
        }),
        distinctUntilChanged(),
        switchMap((searchString) => this.autoCompleteAssets(searchString.toLowerCase())),
        catchError(() => of([]))
      )
      .subscribe((assets: string[]) => {
        this.filteredAssets = assets;
      });

    this.route.queryParams.pipe(skip(1), takeUntil(this.ngUnSubscribe)).subscribe((params: DiagnosticsQueryParams) => {
      if (history.state.source !== 'pn-url-click') {
        return;
      }

      this.form.patchValue(
        {
          searchTypeControl: params.deviceId ? this.searchTypeEnum.DeviceIdFilter : this.searchTypeEnum.AssetIdFilter,
          deviceIdControl: params?.deviceId || null,
          assetInputControl: null,
        },
        { emitEvent: false }
      );

      if (params?.deviceId) {
        this.disableSearch = true;
        this.isSearch = false;
        this.isInputReadonly = true;
      } else {
        this.disableSearch = true;
        this.isSearch = true;
        this.isInputReadonly = false;
      }
    });
  }

  public ngOnDestroy() {
    this.ngUnSubscribe.next();
    this.ngUnSubscribe.complete();
  }

  private autoCompleteAssets(assetId = ''): Observable<string[]> {
    const selectedAssetIdInLowerCase = this.selectedAssetId && this.selectedAssetId.toLowerCase();
    if (!assetId || selectedAssetIdInLowerCase === assetId) {
      this.showSpinner = false;
      return of([]);
    }
    const params = {
      assetId: encodeURIComponent(assetId),
    };
    this.showSpinner = true;
    return this.dataService.assetsAutocomplete(params).pipe(
      tap(() => (this.showSpinner = false)),
      map((res) => {
        const { assets = [] } = res.data || {};
        return assets;
      }),
      catchError(() => {
        this.showSpinner = false;
        return of([]);
      })
    );
  }

  public autoCompleteOptionSelected(event) {
    const value = event.option.value;
    if (value) {
      this.selectedAssetId = value.assetId;
      this.isSearch = true;
      this.disableSearch = false;
    }
  }

  public search() {
    const { searchTypeControl, deviceIdControl } = this.form.value || {};
    const value = searchTypeControl === this.searchTypeEnum.AssetIdFilter ? this.selectedAssetId : deviceIdControl;
    this.disableSearch = true;
    this.isSearch = false;
    this.isInputReadonly = true;
    this.selectedFilter.emit({ selectedFilter: searchTypeControl, value });
  }

  public displayfn(option) {
    return option?.assetName ? `${option?.assetName} (${option?.assetId})` : option?.assetId;
  }

  public clearInput(emit: boolean = true, value?: string) {
    this.clearQueryParams();
    this.resetForm(value, false);

    if (emit) {
      this.clearFilter.emit();
    }
  }

  private resetForm(value: string | null, emitEvent: boolean) {
    this.form.patchValue(
      {
        searchTypeControl: value || this.searchTypeEnum.AssetIdFilter,
        deviceIdControl: null,
        assetInputControl: null,
      },
      { emitEvent }
    );

    this.disableSearch = true;
    this.isSearch = true;
    this.isInputReadonly = false;
  }

  private clearQueryParams(): void {
    const queryParams = { ...this.route.snapshot.queryParams };
    if (queryParams.deviceId) {
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { deviceId: null },
        queryParamsHandling: 'merge',
      });
    }
  }
}
